import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StyledButton } from './styles/StyledButtons';
import { StyledTypography } from './styles/styles';

const SESSION_KEY = 'qrPairingVerifiedAt';
/** How long a re-auth is considered valid (10 minutes). */
const VALID_FOR_MS = 10 * 60 * 1000;
/** URL param that Auth0 redirect returns with. */
const VERIFIED_PARAM = 'qrVerified';

function isRecentlyVerified(): boolean {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  return !isNaN(ts) && Date.now() - ts < VALID_FOR_MS;
}

interface QrPairingGateProps {
  children: React.ReactNode;
}

/**
 * Wraps the QR pairing UI (CrossDeviceLogin) in the logged-in Settings page.
 * Forces the user to re-authenticate with Auth0 (password entry) before the
 * QR code is generated — matching the same security gate used for email changes.
 *
 * Unaffected: the unauthenticated CrossDeviceLoginPage (pre-login flow).
 */
const QrPairingGate: React.FC<QrPairingGateProps> = ({ children }) => {
  const { user, loginWithRedirect } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const [verified, setVerified] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Detect return from Auth0 re-auth redirect
  useEffect(() => {
    if (searchParams.get(VERIFIED_PARAM) === '1') {
      sessionStorage.setItem(SESSION_KEY, String(Date.now()));
      setVerified(true);
      // Remove param so URL stays clean
      const next = new URLSearchParams(searchParams);
      next.delete(VERIFIED_PARAM);
      setSearchParams(next, { replace: true });
      return;
    }
    // Also honour a fresh sessionStorage flag from a previous verify in this tab session
    if (isRecentlyVerified()) {
      setVerified(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerify = () => {
    setRedirecting(true);

    // Build return URL: same page + tab=cross-device + qrVerified=1
    const params = new URLSearchParams(window.location.search);
    params.set('tab', 'cross-device');
    params.set(VERIFIED_PARAM, '1');
    const returnPath = `${window.location.pathname}?${params.toString()}`;
    const redirectUri = `${window.location.origin}/forward?forwardUrl=${encodeURIComponent(returnPath)}`;

    loginWithRedirect({
      authorizationParams: {
        audience: process.env.REACT_APP_BACKEND_AUDIENCE,
        scope: 'openid profile email',
        prompt: 'login', // Force re-entry of credentials
        max_age: 0, // No SSO shortcut
        login_hint: user?.email ?? '',
        screen_hint: 'login',
        redirect_uri: redirectUri,
      },
    });
  };

  if (verified) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        py: 5,
        px: 2,
        maxWidth: 400,
        mx: 'auto',
        textAlign: 'center',
      }}
    >
      <LockIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
      <StyledTypography tone="tone1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
        Verify your identity
      </StyledTypography>
      <StyledTypography tone="tone1" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
        To generate a QR code you must confirm your password. You will be redirected to the login
        page and brought straight back.
      </StyledTypography>
      {redirecting ? (
        <CircularProgress size={28} />
      ) : (
        <StyledButton variant="contained" onClick={handleVerify} startIcon={<LockIcon />}>
          Verify Identity
        </StyledButton>
      )}
    </Box>
  );
};

export default QrPairingGate;
